// The creator dashboard's money pages (wave 10): Earnings, Payouts and a game's item prices. Shown only while selling
// (wave 9); dashboard.ts sends /dashboard/earnings and /dashboard/payouts to the overview otherwise. Amounts are real
// currency: earnings and cash-outs in money's settlement currency, item prices set in USD with an optional EUR price.

import { api, ApiError } from "../api.ts";
import { getCatalogue, type Game } from "../catalogue.ts";
import { clear, errorBox, h, link, section, toast } from "../dom.ts";
import { formatMoney, itemPath, parseMoney, parsePriceInput, priceInputValue, showMoney, type Money } from "../money.ts";
import {
  cashOutResult,
  cashOutView,
  CASHOUT_STATUS_LABEL,
  parseBalance,
  parseCashouts,
  parseEarnings,
  parsePayoutAccount,
  payoutStatusView,
  safeOnboardingUrl,
  shareText,
} from "../payouts.ts";

export function kpi(k: string, v: string, sub?: string): HTMLElement {
  return h("div", { class: "kpi" }, h("div", { class: "k" }, k), h("div", { class: "v" }, v), sub ? h("div", { class: "dim small" }, sub) : null);
}

// ---- statements --------------------------------------------------------------------------------------------------

export interface StatementLine {
  /** a tip's `item_id` is the game's id */
  kind?: "item" | "cosmetic" | "asset" | "tip" | string;
  item_id: string;
  role: "seller" | "affiliate" | string;
  sales: number;
  earned?: Money;
}

/** Subscription pool money for one game in one closed month. */
export interface PoolLine {
  month: string;
  game: string;
  amount?: Money;
}

export interface Statement {
  month: string;
  status: string;
  amount?: Money;
  lines: StatementLine[];
  pool_lines?: PoolLine[];
  note?: string;
}

export function lineLabel(l: StatementLine, gameTitle: (id: string) => string = (id) => id): string {
  if (l.kind === "tip") return `Tips: ${gameTitle(l.item_id)}`;
  return l.role === "affiliate" ? `${l.item_id} (bought in your game)` : l.item_id;
}

// ---- earnings ----------------------------------------------------------------------------------------------------

export function earningsView(): HTMLElement {
  return h(
    "div",
    null,
    h("h1", null, "Earnings"),
    section(null, async (body) => {
      const [rawEarnings, cfg, st, rawBalance] = await Promise.all([
        api<unknown>("money", "/creators/me/earnings").catch(() => null),
        api<unknown>("money", "/config", { auth: false }).catch(() => null),
        api<{ statements?: Statement[] }>("money", "/creators/me/statements").catch(() => ({ statements: [] })),
        api<unknown>("money", "/creators/me/balance").catch(() => null),
      ]);
      const months = parseEarnings(rawEarnings);
      const balance = parseBalance(rawBalance);
      const cat = await getCatalogue();
      const gameTitle = (id: string) => cat.games.find((g) => g.id === id)?.title ?? id;
      const anyTips = months.some((m) => m.tips);
      const statements = Array.isArray(st.statements) ? st.statements : [];
      clear(
        body,
        h(
          "div",
          { class: "kpis" },
          kpi("Available", balance ? showMoney(balance.available) : "—", "Ready to cash out"),
          kpi("Pending", balance ? showMoney(balance.pending) : "—", "Available 14 days after each sale"),
        ),
        h("p", { class: "dim small" }, shareText(cfg), "Card and Stripe fees come out of Banger's share. ", link("/dashboard/payouts", null, "Cash out on the Payouts page"), "."),
        h(
          "div",
          { class: "panel" },
          h("h2", null, "By month"),
          months.length
            ? h(
                "table",
                null,
                h("thead", null, h("tr", null, h("th", null, "Month"), h("th", { class: "num" }, "Earned"), anyTips ? h("th", { class: "num" }, "Of which tips") : null)),
                h(
                  "tbody",
                  null,
                  [...months].reverse().map((m) =>
                    h("tr", null, h("td", null, m.month), h("td", { class: "num" }, showMoney(m.earned)), anyTips ? h("td", { class: "num" }, m.tips ? showMoney(m.tips) : "—") : null),
                  ),
                ),
              )
            : h("p", { class: "dim" }, "No sales yet."),
          anyTips ? h("p", { class: "dim small" }, "Tips are counted in Earned. Players tip in Banger, and tips can't be refunded.") : null,
        ),
        h("div", { class: "panel" }, h("h2", null, "Monthly statements"), statements.length ? statements.map((x) => statementView(x, gameTitle)) : h("p", { class: "dim" }, "Statements are written after each month closes.")),
      );
    }),
  );
}

function statementView(s: Statement, gameTitle: (id: string) => string): HTMLElement {
  const pool = s.pool_lines ?? [];
  return h(
    "details",
    { class: "statement" },
    h("summary", null, h("strong", null, s.month), " · ", showMoney(s.amount), " ", h("span", { class: `badge ${s.status === "payable" ? "ok" : "warn"}` }, s.status === "payable" ? "Payable" : "Held")),
    h(
      "table",
      null,
      h("thead", null, h("tr", null, h("th", null, "Line"), h("th", { class: "num" }, "Sales"), h("th", { class: "num" }, "Earned"))),
      h(
        "tbody",
        null,
        (s.lines ?? []).map((l) =>
          h(
            "tr",
            { class: l.kind === "tip" ? "tip-line" : null },
            h("td", null, l.kind === "tip" ? h("span", { class: "badge" }, "Tip") : null, l.kind === "tip" ? " " : null, lineLabel(l, gameTitle)),
            h("td", { class: "num" }, l.sales),
            h("td", { class: "num" }, showMoney(l.earned)),
          ),
        ),
        pool.map((l) => h("tr", null, h("td", null, `Subscription pool: ${gameTitle(l.game)} (${l.month})`), h("td", { class: "num" }, ""), h("td", { class: "num" }, showMoney(l.amount)))),
      ),
    ),
    s.note ? h("p", { class: "dim small" }, s.note) : null,
    h("button", { type: "button", class: "btn small ghost", onclick: () => downloadCsv(s.month) }, "Download CSV"),
  );
}

async function downloadCsv(month: string): Promise<void> {
  try {
    const res = await api<Response>("money", `/creators/me/statements/${month}?format=csv`, { raw: true });
    if (!res.ok) throw new ApiError(res.status, "csv_failed", "Couldn't get the statement");
    const url = URL.createObjectURL(await res.blob());
    const a = h("a", { href: url, download: `statement-${month}.csv` });
    document.body.append(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  } catch (e) {
    toast(e instanceof Error ? e.message : String(e));
  }
}

// ---- payouts -----------------------------------------------------------------------------------------------------

/** Opens Stripe's onboarding: asks money for a fresh link and goes there (https only). */
async function startOnboarding(button: HTMLButtonElement, msg: HTMLElement): Promise<void> {
  button.disabled = true;
  try {
    const url = safeOnboardingUrl(await api<unknown>("money", "/creators/me/payout-account", { method: "POST", body: {}, idempotent: true }));
    if (!url) throw new Error("Stripe's setup link didn't arrive. Try again in a minute.");
    location.assign(url);
  } catch (e) {
    clear(msg, errorBox(e));
    button.disabled = false;
  }
}

export function payoutsView(): HTMLElement {
  const box = h("div");
  const draw = () =>
    clear(
      box,
      section(null, async (body) => {
        const [acctRaw, balRaw, cashRaw] = await Promise.all([
          api<unknown>("money", "/creators/me/payout-account").catch(() => null),
          api<unknown>("money", "/creators/me/balance").catch(() => null),
          api<unknown>("money", "/creators/me/cashouts").catch(() => null),
        ]);
        const acct = parsePayoutAccount(acctRaw);
        const status = payoutStatusView(acct);
        const balance = parseBalance(balRaw);
        const cash = cashOutView(balance, acct);
        const history = parseCashouts(cashRaw);
        const msg = h("div", { "aria-live": "polite" });
        const onboard = status.action ? (h("button", { type: "button", class: "btn" }, status.action) as HTMLButtonElement) : null;
        onboard?.addEventListener("click", () => void startOnboarding(onboard, msg));
        const cashBtn = cash.button ? (h("button", { type: "button", class: "btn" }, cash.button) as HTMLButtonElement) : null;
        // one key per click: a retry after a lost reply can't pay twice (money's cash-out is idempotent)
        cashBtn?.addEventListener("click", async () => {
          cashBtn.disabled = true;
          try {
            const r = await api<unknown>("money", "/creators/me/cashouts", { method: "POST", body: {}, idempotent: true });
            toast(cashOutResult(r));
            draw();
          } catch (e) {
            clear(msg, errorBox(e));
            cashBtn.disabled = false;
          }
        });
        clear(
          body,
          h(
            "section",
            { class: "panel" },
            h("h2", null, "Payout account ", h("span", { class: `badge ${status.tone === "dim" ? "" : status.tone}` }, status.badge)),
            h("p", null, status.text),
            status.requirements.length ? h("ul", null, status.requirements.map((r) => h("li", null, r))) : null,
            onboard,
            acct?.country ? h("p", { class: "dim small" }, `Account country: ${acct.country}.`) : null,
          ),
          h(
            "section",
            { class: "panel" },
            h("h2", null, "Balance"),
            h(
              "div",
              { class: "kpis" },
              kpi("Available", balance ? showMoney(balance.available) : "—"),
              kpi("Pending", balance ? showMoney(balance.pending) : "—", "Becomes available 14 days after each sale"),
            ),
            cashBtn,
            h("p", { class: "dim small" }, cash.note, " Unpaid earnings also go out on the 1st of each month once you're over the minimum."),
          ),
          msg,
          h(
            "section",
            { class: "panel" },
            h("h2", null, "Cash-outs"),
            history.length
              ? h(
                  "table",
                  null,
                  h("thead", null, h("tr", null, h("th", null, "Date"), h("th", { class: "num" }, "Amount"), h("th", null, "Status"))),
                  h(
                    "tbody",
                    null,
                    history.map((c) =>
                      h(
                        "tr",
                        null,
                        h("td", null, c.createdAt ? new Date(c.createdAt).toISOString().slice(0, 10) : "—"),
                        h("td", { class: "num" }, formatMoney(c.amount.amount_minor, c.amount.currency)),
                        h("td", null, h("span", { class: `badge ${c.status === "paid" ? "ok" : c.status === "failed" ? "warn" : ""}` }, CASHOUT_STATUS_LABEL[c.status])),
                      ),
                    ),
                  ),
                )
              : h("p", { class: "dim" }, "No cash-outs yet."),
          ),
        );
      }),
    );
  draw();
  return h("div", null, h("h1", null, "Payouts"), box);
}

// ---- a game's item prices ----------------------------------------------------------------------------------------

/** Money's in-game item ids: `i_<game without g_>/<item>`. */
export function gameItemId(gameId: string, item: string): string {
  return `i_${gameId.replace(/^g_/, "")}/${item}`;
}

export const ITEM_NAME = /^[a-z0-9][a-z0-9_-]{0,63}$/;

/**
 * The body for `POST /v1/items` (or `PUT /v1/items/:id` to reprice): the USD price, and the EUR price only when the
 * creator typed one (money's price ladder fills it otherwise).
 */
export function itemBody(
  gameId: string,
  item: string,
  name: string,
  usdText: string,
  eurText: string,
): { body: { item_id: string; kind: "item"; name: string; price_usd_minor: number; price_eur_minor?: number } } | { error: string } {
  const it = item.trim();
  if (!ITEM_NAME.test(it)) return { error: "Item ids use lowercase letters, digits, - and _." };
  const nm = name.trim();
  if (!nm || nm.length > 100) return { error: "Give the item a name (up to 100 characters)." };
  const usd = parsePriceInput(usdText, "USD");
  if ("error" in usd) return { error: `US price: ${usd.error}` };
  const body = { item_id: gameItemId(gameId, it), kind: "item" as const, name: nm, price_usd_minor: usd.minor };
  if (!eurText.trim()) return { body };
  const eur = parsePriceInput(eurText, "EUR");
  if ("error" in eur) return { error: `Euro price: ${eur.error}` };
  return { body: { ...body, price_eur_minor: eur.minor } };
}

/** An item's current prices from `GET /v1/items/:id`, or null. */
export function itemPricesOf(raw: unknown): { name: string; usd: Money | null; eur: Money | null } | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as { name?: unknown; prices?: Record<string, unknown> };
  return { name: typeof r.name === "string" ? r.name : "", usd: parseMoney(r.prices?.USD), eur: parseMoney(r.prices?.EUR) };
}

export function itemPricesPanel(g: Game): HTMLElement {
  const item = h("input", { id: "ip-item", required: true, maxlength: 64, autocomplete: "off", spellcheck: "false", placeholder: "sword" }) as HTMLInputElement;
  const name = h("input", { id: "ip-name", required: true, maxlength: 100, placeholder: "Flame sword" }) as HTMLInputElement;
  const usd = h("input", { id: "ip-usd", required: true, inputmode: "decimal", placeholder: "4.99", "aria-describedby": "ip-usd-help" }) as HTMLInputElement;
  const eur = h("input", { id: "ip-eur", inputmode: "decimal", placeholder: "Standard price", "aria-describedby": "ip-eur-help" }) as HTMLInputElement;
  const eurHelp = h("p", { id: "ip-eur-help", class: "dim small" }, "Optional. Leave it empty to use the standard euro price for your US price.");
  const msg = h("div", { "aria-live": "polite" });
  let existing = false;
  // look the item up when its id is typed: an existing item is repriced, and its euro price shows as the default
  item.addEventListener("change", async () => {
    existing = false;
    eur.placeholder = "Standard price";
    if (!ITEM_NAME.test(item.value.trim())) return;
    const r = await api<unknown>("money", itemPath(gameItemId(g.id, item.value.trim()))).then(itemPricesOf, () => null);
    if (!r) return;
    existing = true;
    if (!name.value) name.value = r.name;
    if (r.usd && !usd.value) usd.value = priceInputValue(r.usd.amount_minor);
    if (r.eur) {
      eur.placeholder = priceInputValue(r.eur.amount_minor);
      clear(eurHelp, `Now ${formatMoney(r.eur.amount_minor, "EUR")}. Leave it empty to keep the standard euro price for your US price.`);
    }
  });
  const submit = h("button", { type: "submit", class: "btn" }, "Save price");
  const form = h(
    "form",
    {
      class: "stack",
      onsubmit: async (e: Event) => {
        e.preventDefault();
        const b = itemBody(g.id, item.value, name.value, usd.value, eur.value);
        if ("error" in b) return clear(msg, h("p", { class: "error", role: "alert" }, b.error));
        submit.setAttribute("disabled", "");
        try {
          const r = existing
            ? await api<unknown>("money", itemPath(b.body.item_id), { method: "PUT", body: { name: b.body.name, price_usd_minor: b.body.price_usd_minor, ...(b.body.price_eur_minor ? { price_eur_minor: b.body.price_eur_minor } : {}) }, idempotent: true })
            : await api<unknown>("money", "/items", { body: b.body, idempotent: true });
          const p = itemPricesOf(r);
          existing = true;
          clear(msg, h("p", { class: "badge ok" }, `Saved: ${showMoney(p?.usd ?? { amount_minor: b.body.price_usd_minor, currency: "USD" })}${p?.eur ? ` / ${showMoney(p.eur)}` : ""}`));
        } catch (x) {
          clear(msg, errorBox(x));
        } finally {
          submit.removeAttribute("disabled");
        }
      },
    },
    h("div", { class: "field" }, h("label", { for: "ip-item" }, "Item id"), item, h("p", { class: "dim small" }, "The id your game passes to the purchase SDK.")),
    h("div", { class: "field" }, h("label", { for: "ip-name" }, "Name players see"), name),
    h("div", { class: "field" }, h("label", { for: "ip-usd" }, "Price in US dollars"), usd, h("p", { id: "ip-usd-help", class: "dim small" }, "Includes tax. The lowest price is $0.05.")),
    h("div", { class: "field" }, h("label", { for: "ip-eur" }, "Price in euros"), eur, eurHelp),
    submit,
    msg,
  );
  return h(
    "section",
    { class: "panel item-prices" },
    h("h2", null, "Item prices"),
    h("p", null, "Players pay from their Banger balance, in dollars or euros. You keep 80% of each sale after tax."),
    form,
  );
}

